import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Card, Row, Col, Badge, Alert, Button, Collapse, Table } from 'react-bootstrap'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'
import TextareaWiz from '../../../../../../../common/inputs/wizard/TextareaWiz.jsx'
import InputWiz from '../../../../../../../common/inputs/wizard/InputWiz.jsx'
import DateTimeWiz from '../../../../../../../common/inputs/wizard/DateTimeWiz.jsx'
import SelectWiz from '../../../../../../../common/inputs/wizard/SelectWiz.jsx'
import {
    optionsPrawdopodobienstwo,
    optionsSkutek,
    optionsStatusRyzyka,
} from '../../../../../../../utils/options.js'
import {
    evaluateRiskLevel,
    getOptionLabel,
    getOptionVariant,
} from '../../../../../../../utils/helpers/riskEvaluation.js'

const RiskCard = ({ row, index, wiz, parentName, setRow, kategoria }) => {
    const prefix = `${parentName}.${index}`

    const [showDetails, setShowDetails] = useState(false)

    const riskLevel = evaluateRiskLevel(row?.prawdopodobienstwo, row?.skutek)
    const statusVariant = getOptionVariant(optionsStatusRyzyka, row?.status)
    const riskLevelText = riskLevel.text || ''

    const isCategoryB = kategoria === 'B'

    React.useEffect(() => {
        if ((row?.poziom ?? '') !== riskLevelText) {
            setRow('poziom', riskLevelText)
        }
    }, [riskLevelText, row?.poziom])

    return (
        <Card className={`mb-4 shadow-sm border-${statusVariant}`} style={{ borderLeftWidth: '6px' }}>
            <Card.Header className="bg-light py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex flex-column">
                    <span className="fw-bold text-dark fs-5">
                        <Badge bg="secondary" className="me-2 p-2">#{index + 1}</Badge>
                        {row?.ryzyko || 'Nowe ryzyko'}
                    </span>
                    <small className="text-muted mt-1 ms-4">
                        Kategoria: {row?.kategoria || '-'}
                    </small>
                </div>

                <div className="d-flex flex-column align-items-end justify-content-between gap-2">
                    <Badge bg={riskLevel.severity} className="px-3 py-2">
                        {riskLevel.text}
                    </Badge>
                    <Badge bg={statusVariant} className="px-3 py-2">
                        {row?.status || 'Brak statusu'}
                    </Badge>
                </div>
            </Card.Header>

            <Card.Body>
                <Row className="g-3">
                    <Col md={4}>
                        <SelectWiz
                            wiz={wiz}
                            keyName={`${prefix}.status`}
                            label="Aktualny Status Ryzyka"
                            options={optionsStatusRyzyka}
                            placeholder="Wybierz status..."
                            row={row}
                            setRow={setRow}
                            idx={index}
                        />
                    </Col>
                    <Col md={4}>
                        <InputWiz
                            wiz={wiz}
                            keyName={`${prefix}.osoba_monitorujaca`}
                            label="Osoba monitorująca"
                            row={row}
                            setRow={setRow}
                            idx={index}
                            value="Kierownik projektu"
                            disabled
                        />
                    </Col>

                    {row?.status === 'Wystąpiło' && !isCategoryB && (
                        <Col md={4}>
                            <DateTimeWiz
                                wiz={wiz}
                                keyName={`${prefix}.termin_dzialan_zapobiegawczych`}
                                label="Termin działań zapobiegawczych"
                                hideTime={true}
                                row={row}
                                setRow={setRow}
                                idx={index}
                            />
                        </Col>
                    )}
                    {row?.status === 'Wystąpiło' && (
                        <Col md={12}>
                            <TextareaWiz
                                wiz={wiz}
                                keyName={`${prefix}.przyczyna`}
                                label="Przyczyna"
                                rows={2}
                                row={row}
                                setRow={setRow}
                                idx={index}
                            />
                        </Col>
                    )}

                    {row?.status === 'Wystąpiło' && isCategoryB && (
                        <Col md={12}>
                            <TextareaWiz
                                wiz={wiz}
                                keyName={`${prefix}.propozycja`}
                                label="Propozycja rozwiązania problemu / Działania zapobiegawcze"
                                rows={3}
                                row={row}
                                setRow={setRow}
                                idx={index}
                            />
                        </Col>
                    )}

                    {!isCategoryB && (
                        <Col md={12}>
                            <TextareaWiz
                                wiz={wiz}
                                keyName={`${prefix}.propozycja`}
                                label="Propozycja rozwiązania problemu / Działania zapobiegawcze"
                                rows={3}
                                row={row}
                                setRow={setRow}
                                idx={index}
                            />
                        </Col>
                    )}

                    {!isCategoryB && (
                        <Col md={12} className="d-flex justify-content-end">
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowDetails((v) => !v)}
                                className="d-flex align-items-center gap-2"
                            >
                                {showDetails ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                                {showDetails ? <ChevronUp /> : <ChevronDown />}
                            </Button>
                        </Col>
                    )}

                    {!isCategoryB && (
                        <Col md={12}>
                            <Collapse in={showDetails}>
                                <div>
                                    <Table bordered size="sm" className="mb-0">
                                        <tbody>
                                            <tr>
                                                <th style={{ width: '30%' }}>Kategoria</th>
                                                <td>{row?.kategoria || '-'}</td>
                                            </tr>
                                            <tr>
                                                <th>Nazwa ryzyka</th>
                                                <td>{row?.ryzyko || '-'}</td>
                                            </tr>
                                            <tr>
                                                <th>Przyczyna</th>
                                                <td>{row?.przyczyna || '-'}</td>
                                            </tr>
                                            <tr>
                                                <th>Prawdopodobieństwo</th>
                                                <td>{getOptionLabel(optionsPrawdopodobienstwo, row?.prawdopodobienstwo)}</td>
                                            </tr>
                                            <tr>
                                                <th>Skutek</th>
                                                <td>{getOptionLabel(optionsSkutek, row?.skutek)}</td>
                                            </tr>
                                            <tr>
                                                <th>Poziom ryzyka</th>
                                                <td>{riskLevel.text}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            </Collapse>
                        </Col>
                    )}
                </Row>
            </Card.Body>
        </Card>
    )
}

RiskCard.propTypes = {
    row: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    wiz: PropTypes.any.isRequired,
    parentName: PropTypes.string.isRequired,
    setRow: PropTypes.func.isRequired,
    kategoria: PropTypes.string,
}

export const RyzykaRepeater = (props) => {
    const parent = props.parentListName || 'ryzyka_list'
    const wiz = props.wiz

    const risks = wiz?.form?.[parent] || []
    const kategoria = wiz?.form?.kategoria

    const setRowAtIndex = (index) => (key, value) => {
        const next = Array.isArray(risks) ? [...risks] : []
        const currentRow = next[index] || {}
        if (currentRow?.[key] === value) {
            return
        }
        next[index] = {
            ...currentRow,
            [key]: value,
        }
        wiz.setValue(parent, next)
    }

    return (
        <div className="mt-4">
            {risks.length === 0 && (
                <Alert variant="info">
                    Brak zdefiniowanych ryzyk.
                </Alert>
            )}

            {Array.isArray(risks) && risks.map((row, idx) => (
                <RiskCard
                    key={idx}
                    index={idx}
                    row={row}
                    wiz={wiz}
                    parentName={parent}
                    setRow={setRowAtIndex(idx)}
                    kategoria={kategoria}
                />
            ))}
        </div>
    )
}

RyzykaRepeater.propTypes = {
    wiz: PropTypes.any.isRequired,
    parentListName: PropTypes.string,
}

export default RyzykaRepeater
